import { useEffect, useState } from 'react'

/** El corte de «una columna» del documento de diseño: por debajo de 640 px la página es angosta. */
export const ANGOSTO = 639.98

/** El corte del escritorio completo: por debajo de 1200 px el documento ya no dibuja tres columnas. */
export const COMPACTO = 1199.98

/**
 * ¿La ventana está por debajo del corte de una columna?
 *
 * Casi todo el responsive de este proyecto se resuelve en CSS, y así debe seguir: una media query
 * no se puede desincronizar del árbol. Esto existe para el caso que la CSS no alcanza — cuando lo
 * que cambia no es el ASPECTO de un elemento sino DÓNDE VIVE en el documento.
 *
 * El caso concreto: el panel de decisión de la bandeja. En escritorio es la columna de la derecha,
 * hermana de la lista; en angosto el diseño lo abre DENTRO de la fila, empujando el resto («no se
 * vuelve modal»). Un elemento no puede estar a la vez dentro de un `<li>` y ser hermano del `<ul>`,
 * y no hay propiedad de CSS que lo mueva de sitio. Las dos salidas sin JavaScript eran pintarlo dos
 * veces y ocultar una —dos peticiones del veredicto, o el doble de nodos vivos— o sacarlo de flujo
 * con posicionamiento absoluto, que cambiaría el escritorio ya aprobado por una rejilla frágil.
 *
 * Se emplea `matchMedia` y no `resize`: el navegador avisa cuando se CRUZA el umbral, no en cada
 * píxel de arrastre. `addEventListener('change')` con su limpieza; nada que medir a mano.
 */
export function usarAngosto(): boolean {
  const [angosto, setAngosto] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia(`(max-width: ${ANGOSTO}px)`).matches,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${ANGOSTO}px)`)
    const alCambiar = (e: MediaQueryListEvent) => setAngosto(e.matches)
    setAngosto(mq.matches)      // por si cambió entre el primer render y el efecto
    mq.addEventListener('change', alCambiar)
    return () => mq.removeEventListener('change', alCambiar)
  }, [])
  return angosto
}


/**
 * ¿La ventana está por debajo del corte del escritorio completo (1200 px)?
 *
 * Lo usa el masthead, y no comparte corte con `usarAngosto` a propósito. Medido con el peor caso
 * real —cuenta de administración y una razón social de sesenta caracteres, que las hay en SECOP— la
 * fila de la cabecera no cabe en 640 ni en 820: se parte en dos renglones y el `<header>` pasa de
 * 62 a 116 px. Y de `--alto-masthead` cuelga todo lo pegajoso, así que partirse no es un problema
 * estético: deja la franja de veredicto de la ficha anclada por dentro de la propia cabecera.
 *
 * El documento respalda el corte: su marco de 820 px dibuja la cabecera con marca, empresa,
 * buscador y cifras — SIN pestañas y sin los enlaces secundarios. O sea que a ese ancho la
 * navegación ya va plegada.
 */
export function usarCompacto(): boolean {
  const [compacto, setCompacto] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia(`(max-width: ${COMPACTO}px)`).matches,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${COMPACTO}px)`)
    const alCambiar = (e: MediaQueryListEvent) => setCompacto(e.matches)
    setCompacto(mq.matches)
    mq.addEventListener('change', alCambiar)
    return () => mq.removeEventListener('change', alCambiar)
  }, [])
  return compacto
}
